export enum AbbyAccessPolicy {
  CARE_OPS = 'CareOps Access Policy',
  CLINICAL = 'Clinical Access Policy',
  CAREGIVER = 'Caregiver Access Policy',
  CHAP_AUDITOR = 'CHAP Auditor Access Policy',
  BILLING = 'Billing Access Policy',
  GM = 'GM Access Policy',
  FINANCE = 'Finance Access Policy',
  FGA = 'FGA Access Policy',
  SURVEYOR = 'Surveyor Access Policy',
  ADMIN = 'Admin'
}

export const parseAccessPolicies = (accessPolicyList: string[]): AbbyAccessPolicy[] => {
  const policies: Set<AbbyAccessPolicy> = new Set()

  for (const policy of accessPolicyList) {
    const policyId = policy.split('/')[1]

    // CARE_OPS
    if (
      [
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CARE_OPS,
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CARE_OPS_RBAC
      ].includes(policyId)
    ) {
      policies.add(AbbyAccessPolicy.CARE_OPS)
    }

    //CLINICAL
    if (
      [
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CLINICAL,
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CLINICAL_RBAC
      ].includes(policyId)
    ) {
      policies.add(AbbyAccessPolicy.CLINICAL)
    }

    // CAREGIVER
    if (
      [
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CAREGIVER_PATIENT_PARAMETERIZED,
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CAREGIVER_PARAMETERIZED
      ].includes(policyId)
    ) {
      policies.add(AbbyAccessPolicy.CAREGIVER)
    }

    // CHAP AUDITOR
    if (
      [
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CHAP_AUDIT_PATIENT_PARAMETERIZED,
        process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_CHAP_AUDIT_CAREGIVER_PARAMETERIZED
      ].includes(policyId)
    ) {
      policies.add(AbbyAccessPolicy.CHAP_AUDITOR)
    }

    // BILLING
    if ([process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_BILLING_PARAMETERIZED].includes(policyId)) {
      policies.add(AbbyAccessPolicy.BILLING)
    }

    // GM
    if ([process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_GM_PARAMETERIZED].includes(policyId)) {
      policies.add(AbbyAccessPolicy.GM)
    }

    // FINANCE
    if ([process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_FINANCE_PARAMETERIZED].includes(policyId)) {
      policies.add(AbbyAccessPolicy.FINANCE)
    }

    // FGA
    if ([process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_FGA].includes(policyId)) {
      policies.add(AbbyAccessPolicy.FGA)
    }

    // SURVEYOR
    const surveyorPolicyId = process.env.NEXT_PUBLIC_ACCESS_POLICY_ID_SURVEYOR
    if (policyId && surveyorPolicyId && policyId === surveyorPolicyId) {
      policies.add(AbbyAccessPolicy.SURVEYOR)
    }
  }
  return Array.from(policies)
}

export const isAccessPolicyAdmin = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.includes(AbbyAccessPolicy.ADMIN)
}
export const isAccessPolicyAdminOrCareopsOrClinical = (
  accessPolicyList: AbbyAccessPolicy[]
): boolean => {
  return [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.CARE_OPS, AbbyAccessPolicy.CLINICAL].some(
    (policy) => accessPolicyList.includes(policy)
  )
}

export const isAccessPolicyAdminOrClinical = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.CLINICAL].some((policy) =>
    accessPolicyList.includes(policy)
  )
}

export const isAccessPolicyAdminOrCareops = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.CARE_OPS].some((policy) =>
    accessPolicyList.includes(policy)
  )
}

export const isAccessPolicyBilling = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.includes(AbbyAccessPolicy.BILLING)
}

export const isAccessPolicyFinance = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.includes(AbbyAccessPolicy.FINANCE)
}

export const isAccessPolicyGM = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.includes(AbbyAccessPolicy.GM)
}

export const isAccessPolicyCaregiver = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.includes(AbbyAccessPolicy.CAREGIVER)
}

export const isAccessPolicyFGA = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.includes(AbbyAccessPolicy.FGA)
}

export const isAccessPolicyAdminOrFGA = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.FGA].some((policy) =>
    accessPolicyList.includes(policy)
  )
}

export const isFGAOnlyPolicy = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.length === 1 && accessPolicyList.includes(AbbyAccessPolicy.FGA)
}

export const isAccessPolicyAdminOrSurveyor = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.SURVEYOR].some((policy) =>
    accessPolicyList.includes(policy)
  )
}

export const isSurveyorOnlyPolicy = (accessPolicyList: AbbyAccessPolicy[]): boolean => {
  return accessPolicyList.length === 1 && accessPolicyList.includes(AbbyAccessPolicy.SURVEYOR)
}

export function canManageOnboardingAssignees({
  isAdmin,
  accessPolicyList,
  authorizedStates,
  caregiverState
}: {
  isAdmin: boolean
  accessPolicyList: AbbyAccessPolicy[]
  authorizedStates: string[]
  caregiverState?: string | null
}): boolean {
  if (isAdmin) {
    return true
  }
  if (!caregiverState) {
    return false
  }
  if (!authorizedStates.includes(caregiverState)) {
    return false
  }
  return isAccessPolicyFGA(accessPolicyList) || accessPolicyList.includes(AbbyAccessPolicy.CARE_OPS)
}
